import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const SellerClaimList = () => {
  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [claims, setClaims] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get('/api/seller/claim/list-data', {
        withCredentials: true,
      });

      if (res.data && res.data.ok) {
        setShopName(res.data.shop_name || '');
        setClaims(Array.isArray(res.data.claims) ? res.data.claims : []);
      } else {
        alert(res.data?.error || '클레임 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error;
      if (redirect) {
        window.location.href = redirect;
        return;
      }
      alert(error || '클레임 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimProcess = async (claim) => {
    try {
      const res = await axios.post(
        '/api/seller/claim/process-json',
        {
          claim_id: claim.claim_id,
          status: claim.status || '',
          admin_memo: claim.admin_memo || '',
        },
        { withCredentials: true }
      );

      if (res.data && res.data.ok) {
        setMessage(res.data.message || '처리 완료되었습니다.');
        fetchData();
      } else {
        alert(res.data?.error || '처리에 실패했습니다.');
      }
    } catch (err) {
      alert(err?.response?.data?.error || '처리에 실패했습니다.');
    }
  };

  const handleFieldChange = (claimId, field, value) => {
    setClaims((prev) =>
      prev.map((item) =>
        item.claim_id === claimId ? { ...item, [field]: value } : item
      )
    );
  };

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString('ko-KR');
  };

  const renderStatusBadge = (status) => {
    if (status === 'approved') return <span style={styles.badgeSuccess}>승인</span>;
    if (status === 'rejected') return <span style={styles.badgeDanger}>반려</span>;
    if (status === 'completed') return <span style={styles.badgePrimary}>완료</span>;
    return <span style={styles.badgeSecondary}>요청됨</span>;
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.loading}>로딩중...</div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <div style={styles.container}>
        <h2 style={styles.title}>클레임 목록</h2>

        {message ? (
          <div style={styles.infoAlert}>{message}</div>
        ) : null}

        {claims.length > 0 ? (
          claims.map((claim) => (
            <div key={claim.claim_id} style={styles.card}>
              <div style={styles.cardHeader}>
                <strong>상품: {claim.product_name || ''}</strong>
              </div>

              <div style={styles.cardBody}>
                <div style={styles.mb2}>
                  <p style={styles.p}><strong>신청 타입:</strong> {claim.type || ''}</p>
                  <p
                    style={styles.p}
                    dangerouslySetInnerHTML={{
                      __html: `<strong>신청 이유:</strong> ${String(claim.reason || '').replace(/\n/g, '<br>')}`
                    }}
                  />
                  <p style={styles.p}><strong>신청 날짜:</strong> {claim.created_at || ''}</p>

                  <p style={styles.p}>
                    <strong>현재 상태:</strong> {renderStatusBadge(claim.status)}
                  </p>

                  {claim.image_url ? (
                    <img
                      src={claim.image_url}
                      alt="첨부 이미지"
                      style={styles.claimImage}
                    />
                  ) : null}
                </div>

                <div style={styles.orderBox}>
                  <h6 style={styles.subTitle}>주문서 정보</h6>
                  <p style={styles.p}>주문 번호: {claim.order_id || ''}</p>
                  <p style={styles.p}>주문자명: {claim.buyer_name || ''}</p>
                  <p style={styles.p}>연락처: {claim.buyer_phone || ''}</p>
                  <p style={styles.p}>배송 주소: {claim.address || ''}</p>
                  <p style={styles.p}>
                    결제 금액:{' '}
                    <span style={styles.priceText}>
                      {formatNumber(claim.result_price)} 원
                    </span>
                  </p>
                </div>

                <div style={styles.formArea}>
                  <div style={styles.formRow}>
                    <label style={styles.labelCol}>처리 상태</label>
                    <div style={styles.inputCol}>
                      <select
                        value={claim.status || ''}
                        onChange={(e) =>
                          handleFieldChange(claim.claim_id, 'status', e.target.value)
                        }
                        style={styles.select}
                        required
                      >
                        <option value="">선택</option>
                        <option value="approved">승인</option>
                        <option value="rejected">반려</option>
                        <option value="completed">완료</option>
                      </select>
                    </div>
                  </div>

                  <div style={styles.formRow}>
                    <label style={styles.labelCol}>관리자 메모</label>
                    <div style={styles.inputCol}>
                      <textarea
                        rows="3"
                        placeholder="관리자 메모"
                        value={claim.admin_memo || ''}
                        onChange={(e) =>
                          handleFieldChange(claim.claim_id, 'admin_memo', e.target.value)
                        }
                        style={styles.textarea}
                      />
                    </div>
                  </div>

                  <div style={styles.textEnd}>
                    <button
                      type="button"
                      style={styles.primaryBtn}
                      onClick={() => handleClaimProcess(claim)}
                    >
                      처리
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.emptyAlert}>등록된 클레임이 없습니다.</div>
        )}
      </div>
    </SellerLayout>
  );
};

const styles = {
  loading: { padding: '40px 0' },
  container: { marginTop: '16px' },
  title: { marginBottom: '24px' },
  infoAlert: {
    marginBottom: '16px',
    padding: '12px 14px',
    border: '1px solid #b6d4fe',
    background: '#cfe2ff',
    color: '#084298',
    borderRadius: '8px',
  },
  emptyAlert: {
    padding: '12px 14px',
    border: '1px solid #dee2e6',
    background: '#f8f9fa',
    borderRadius: '8px',
  },
  card: {
    border: '1px solid #dee2e6',
    borderRadius: '10px',
    boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)',
    marginBottom: '24px',
    background: '#fff',
  },
  cardHeader: {
    padding: '12px 16px',
    background: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
  },
  cardBody: {
    padding: '16px',
  },
  mb2: { marginBottom: '12px' },
  p: { margin: '0 0 8px 0' },
  claimImage: {
    width: '400px',
    maxWidth: '100%',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
  },
  orderBox: {
    borderTop: '1px solid #dee2e6',
    paddingTop: '16px',
    marginTop: '16px',
  },
  subTitle: {
    fontWeight: 700,
    marginBottom: '8px',
  },
  priceText: {
    color: '#0d6efd',
    fontWeight: 700,
  },
  formArea: {
    marginTop: '16px',
  },
  formRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  labelCol: {
    width: '120px',
    fontWeight: 600,
    paddingTop: '8px',
  },
  inputCol: {
    flex: 1,
    minWidth: '240px',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    background: '#fff',
  },
  textarea: {
    width: '100%',
    minHeight: '90px',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  textEnd: {
    textAlign: 'right',
  },
  primaryBtn: {
    border: 'none',
    background: '#0d6efd',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  badgeSuccess: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '999px',
    background: '#198754',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
  },
  badgeDanger: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '999px',
    background: '#dc3545',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
  },
  badgePrimary: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '999px',
    background: '#0d6efd',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
  },
  badgeSecondary: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '999px',
    background: '#6c757d',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
  },
};

export default SellerClaimList;